import React from "react";
import Keiji from "../nohImg/Keiji.jpg";
import Nityakishore from "../nohImg/Nityakishore.JPG";
import Tikashi from "../nohImg/Tikashi.png";
import Osamm from "../nohImg/Osamm.jpeg";
import Katuzaka from "../nohImg/Kazutaka.jpeg";
import Atsushi from "../nohImg/Atshushi.jpeg";
import { motion } from "framer-motion";

const ExecutiveTeamSection = () => {
  return (
    <div className="px-4 sm:px-6 xl:px-20 py-4 bg-gradient-to-b from-gray-50 to-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto mt-8 mb-8">
        {/* HEADING */}
        <div className="text-center mb-14">
          <h2 className="text-2xl xl:text-4xl font-semibold text-gray-900 mb-6 tracking-wide">
            Our Executive Team
          </h2>
          <p className="text-gray-600 text-md md:text-lg leading-relaxed max-w-2xl mx-auto text-justify">
            Our executive leaders bring decades of combined experience in fire
            safety, providing stakeholders with unwavering{" "}
            <span className="text-green-700 font-semibold">Trust</span> and{" "}
            <span className="text-green-700 font-semibold">Safety</span> through
            innovation and dedication.
          </p>
        </div>

        {/* FIRST ROW */}
        <div className="mt-10 flex flex-col lg:flex-row items-start gap-8 lg:gap-12">
          <div className="w-full lg:w-[65%] px-0 sm:px-2">
            <motion.div
              initial={{ opacity: 0, x: -80 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="bg-white rounded-xl p-6 sm:p-8 border border-gray-200 relative"
            >
              {/* decorative arrow — hidden on smaller screens to avoid overflow/misalignment */}
              <div
                className="hidden lg:block absolute top-1/2 translate-y-20 -right-9 w-0 h-0
                border-t-[30px] border-t-transparent
                border-b-[36px] border-b-transparent
                border-l-[36px] border-l-white
                drop-shadow-sm"
              ></div>

              <h1 className="font-semibold text-xl sm:text-2xl">
                Message from MD’s Desk
              </h1>
              <br />
              <p className="text-gray-700 text-sm sm:text-md md:text-lg text-justify leading-relaxed">
                {" "}
                Our mission was born from the tragedy of the 1923 Great Kanto Earthquake in Japan,
                where an estimated 105,000 lives were lost, primarily to fire. Driven by a resolve to
                ensure such a catastrophe would never happen again, our founder, Mr. Teruichi Nohmi,
                pioneered Japan’s fire protection industry—marking the beginning of our enduring
                commitment to disaster prevention.{" "}
                <br />
                For over a century, Nohmi Bosai has stood as a guardian of lives and property.
                Through continuous innovation and the rigorous standards of Japanese quality,
                we have significantly advanced fire safety standards across Japan.
                <br />
                In 2008, we brought this rich heritage and expertise to India.
                As India undergoes rapid urbanization and economic growth, our role has
                evolved beyond fire protection. Today, we deliver comprehensive, resilient
                solutions designed to mitigate diverse hazards and safeguard the nation's progress.{" "}
                <br />
                We are dedicated to contributing to India’s continued growth by providing advanced,
                highly reliable, and accessible safety systems. By leveraging our global experience
                alongside deep local insights, we remain a trusted partner in protecting
                lives, securing assets, and fostering sustainable development.
                <br />
                Together, let us build a safer future.
              </p>
            </motion.div>
          </div>

          {/* RIGHT IMAGE + NAME + DESIGNATION */}
          <motion.div
            initial={{ opacity: 0, x: 80 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="w-full lg:w-[35%] flex flex-col justify-center items-center mx-auto"
          >
            <motion.img
              src={Atsushi}
              alt="Managing Director"
              className="w-full h-auto max-w-[280px] sm:max-w-[320px] lg:max-w-[350px] object-cover rounded-xl shadow-xl shadow-gray-950 border border-gray-200"
            />

            <motion.div
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="bg-[#011b35] text-white px-4 sm:px-6 py-3 mt-2 rounded-xl w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[350px] text-center relative"
            >
              <motion.h3
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="text-base sm:text-lg tracking-widest text-[#92bfec]"
              >
                MANAGING DIRECTOR
              </motion.h3>

              <motion.p
                initial={{ opacity: 0, scale: 0.5, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}
                className="text-xl sm:text-2xl -mt-1"
              >
                Atsushi Hirosawa
              </motion.p>
            </motion.div>
          </motion.div>
        </div>

        {/* SECOND ROW */}

        <div className="mt-16 sm:mt-20 flex flex-col items-center px-0 sm:px-6 lg:px-16 w-full max-w-7xl mx-auto">
          {/* Directors Grid */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 justify-items-center items-start w-full"
          >
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center"
            >
              <img
                src={Katuzaka}
                alt="Katuzaka"
                className="w-28 sm:w-32 md:w-36 lg:w-40 h-32 sm:h-36 md:h-40 lg:h-44 object-cover rounded-md shadow-xl shadow-gray-950"
              />
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mt-3 text-center">
                Kazutaka Suzuki
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-md">Director</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col items-center"
            >
              <img
                src={Osamm}
                alt="Osamu Isshiki"
                className="w-28 sm:w-32 md:w-36 lg:w-40 h-32 sm:h-36 md:h-40 lg:h-44 object-cover rounded-md shadow-xl shadow-gray-950"
              />
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mt-3 text-center">
                Osamu Isshiki
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-md">Director</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col items-center"
            >
              <img
                src={Tikashi}
                alt="Takashi"
                className="w-28 sm:w-32 md:w-36 lg:w-40 h-32 sm:h-36 md:h-40 lg:h-44 object-cover rounded-md shadow-xl shadow-gray-950"
              />
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mt-3 text-center">
                Takashi Ito
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-md">Director</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col items-center"
            >
              <img
                src={Nityakishore}
                alt="Nityakishore"
                className="w-28 sm:w-32 md:w-36 lg:w-40 h-32 sm:h-36 md:h-40 lg:h-44 object-cover rounded-md shadow-xl shadow-gray-950"
              />
              <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-800 mt-3 text-center">
                Nityakishore Vashist
              </h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-md">Director</p>
            </motion.div>
          </motion.div>

          {/* Message Box */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            viewport={{ once: true }}
            className="relative bg-white border border-gray-300 p-5 sm:p-6 md:p-8 rounded-xl text-justify w-full lg:w-[85%] xl:w-[75%] mt-12 mb-4"
          >
            <p className="text-gray-700 text-sm sm:text-md md:text-lg leading-relaxed">
              With a combined experience of over a century in the fire
              protection industry, our leadership team remains devoted to
              delivering cutting-edge safety solutions while upholding the
              highest standards of quality, innovation, and reliability that{" "}
              <span className="font-semibold text-green-700">Nohmi Bosai</span>{" "}
              represents worldwide.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveTeamSection;